import { Linking, Platform } from 'react-native';

/**
 * CORE-68 / CORE-1237 — is this address one of ours?
 *
 * `Linking.openURL` on react-native-web expands to `window.open(url, '_blank')`
 * — a NEW TAB. For a foreign link that is right; for our own page it is not:
 * on our own address the person expects a navigation in this tab, not a second
 * copy of the app, and any popup blocker turns the tap into "nothing happened"
 * without a single error in the console.
 *
 * Origin is compared, not a string prefix: `startsWith(origin)` would accept
 * `https://app.agapella.io.evil.tld`. A malformed address is not ours — better
 * to let it take the old path than to try to navigate nowhere. `javascript:`
 * and `data:` have a `null` origin per spec, so they are rejected here too.
 */
export function isSameOriginUrl(url: string, origin: string): boolean {
  try {
    return new URL(url, origin).origin === new URL(origin).origin;
  } catch {
    return false;
  }
}

/**
 * CORE-1237 — open a URL the way the person expects.
 *
 * On web, an address of our own origin navigates in THIS tab (state of a
 * second app copy is worth nothing, and popup blockers cannot get in the
 * way). Everything else — foreign links on web, and every link on native —
 * goes through `Linking.openURL` as before. The rejection is swallowed: a
 * link nobody can handle must not crash the tap.
 *
 * Shared by the chat threads, the notifications screen and the master
 * profile so there is exactly one answer to "is this ours" in the app.
 */
export function openUrlInApp(url: string): Promise<void> {
  if (Platform.OS === 'web' && typeof window !== 'undefined' && isSameOriginUrl(url, window.location.origin)) {
    window.location.assign(url);
    return Promise.resolve();
  }
  return Linking.openURL(url).catch(() => undefined);
}
